import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

// Query keys
export const auditKeys = {
  all: ['audit'] as const,
  entity: (entityName: string, entityId: string) => [...auditKeys.all, 'entity', entityName, entityId] as const,
};

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// Audit log entry from API
export interface AuditLogEntry {
  id: number;
  entity: string;
  entityName: string;
  entityId: string;
  action: string;
  userId: string | null;
  userName: string | null;
  timestamp: string;
  oldValues: string | null;
  newValues: string | null;
  differences: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
}

// Parsed differences for display
export interface AuditDifference {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

// Parse differences JSON into displayable format
// Backend saves as: { "PropertyName": { "OldValue": x, "NewValue": y } }
export function parseAuditDifferences(differences: string | null): AuditDifference[] {
  if (!differences) return [];
  try {
    const parsed = JSON.parse(differences);

    // Handle array format (legacy)
    if (Array.isArray(parsed)) {
      return parsed.map((diff: { PropertyName?: string; Field?: string; OldValue?: unknown; NewValue?: unknown }) => ({
        field: diff.PropertyName || diff.Field || 'Unknown',
        oldValue: diff.OldValue !== undefined ? String(diff.OldValue) : null,
        newValue: diff.NewValue !== undefined ? String(diff.NewValue) : null,
      }));
    }

    // Handle object/dictionary format (current backend format)
    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed).map(([field, values]) => {
        const typedValues = values as { OldValue?: unknown; NewValue?: unknown };
        return {
          field,
          oldValue: typedValues.OldValue !== undefined ? String(typedValues.OldValue) : null,
          newValue: typedValues.NewValue !== undefined ? String(typedValues.NewValue) : null,
        };
      });
    }

    return [];
  } catch {
    return [];
  }
}

// Get audit history for an entity
export function useAuditHistory(entityName: string, entityId: string, enabled = true) {
  return useQuery({
    queryKey: auditKeys.entity(entityName, entityId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<AuditLogEntry[]>>(
        `/api/audit/entity/${encodeURIComponent(entityName)}/${encodeURIComponent(entityId)}`
      );
      return response.data.data;
    },
    enabled: enabled && !!entityName && !!entityId,
  });
}
